import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Waves, Instagram, Globe } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const location = useLocation();

  // Auto-scroll to top when switching tabs/routes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // Simple Google Translate implementation
  const handleTranslate = (langCode, langName) => {
    setCurrentLanguage(langCode);
    
    if (langCode === 'en') {
      // Reload page to return to original English
      window.location.reload();
      return;
    }

    // Use Google Translate - simpler approach
    const currentUrl = encodeURIComponent(window.location.href);
    const translateUrl = `https://translate.google.com/translate?sl=en&tl=${langCode}&u=${currentUrl}`;
    window.open(translateUrl, '_blank');
    
    setTranslateOpen(false);
  };

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Threats', href: '/threats' },
    { name: 'Action', href: '/action' },
    { name: 'Resources', href: '/resources' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/90 backdrop-blur-md border-b border-gray-700/50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="p-2 bg-blue-600 rounded-lg group-hover:bg-blue-700 transition-colors duration-200">
              <Waves className="h-6 w-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white">
                Fight for Vaquita
              </span>
              <span className="text-xs text-gray-400 -mt-1">
                10 Vaquitas, 1 Ocean, 0 Time
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors duration-200 hover:text-blue-400 ${
                  location.pathname === item.href
                    ? 'text-blue-400'
                    : 'text-gray-300'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Instagram Link */}
            <a 
              href="https://instagram.com/fightforvaquita" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-pink-400 transition-colors duration-200"
              title="Follow us on Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>

            {/* Google Translate */}
            <div className="relative">
              <button
                onClick={() => setTranslateOpen(!translateOpen)}
                className="text-gray-300 hover:text-blue-400 transition-colors duration-200 flex items-center space-x-1"
                title="Translate Page"
              >
                <Globe className="h-5 w-5" />
                <span className="text-sm font-medium hidden sm:block">Translate</span>
                {currentLanguage !== 'en' && (
                  <span className="text-xs bg-blue-600 text-white px-1 rounded">
                    {currentLanguage.toUpperCase()}
                  </span>
                )}
              </button>
              
              {translateOpen && (
                <div className="absolute right-0 top-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-4 z-50 min-w-[220px]">
                  <div className="text-xs text-gray-400 mb-2">Choose Language:</div>
                  <div className="space-y-1">
                    {[
                      { code: 'en', name: 'English', native: 'English' },
                      { code: 'es', name: 'Spanish', native: 'Español' },
                      { code: 'fr', name: 'French', native: 'Français' },
                      { code: 'de', name: 'German', native: 'Deutsch' },
                      { code: 'pt', name: 'Portuguese', native: 'Português' },
                      { code: 'zh', name: 'Chinese', native: '中文' },
                      { code: 'ja', name: 'Japanese', native: '日本語' },
                      { code: 'ru', name: 'Russian', native: 'Русский' },
                      { code: 'ar', name: 'Arabic', native: 'العربية' },
                      { code: 'hi', name: 'Hindi', native: 'हिन्दी' }
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleTranslate(lang.code, lang.name)}
                        className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                          currentLanguage === lang.code 
                            ? 'bg-blue-600 text-white' 
                            : 'text-gray-300 hover:text-white hover:bg-gray-700'
                        }`}
                      >
                        <span className="font-medium">{lang.native}</span>
                        {lang.code !== lang.name.toLowerCase() && (
                          <span className="text-xs text-gray-400 ml-2">({lang.name})</span>
                        )}
                      </button>
                    ))}
                  </div>
                  
                  <div className="mt-3 pt-2 border-t border-gray-700">
                    <p className="text-xs text-gray-400 leading-tight">
                      Uses Google Translate for universal compatibility
                    </p>
                  </div>
                  
                  <button 
                    onClick={() => setTranslateOpen(false)}
                    className="mt-2 text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
            
            <Link to="/action">
              <Button className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded transition-colors duration-200">
                Take Action
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-3">
            {/* Mobile Instagram & Translate */}
            <a 
              href="https://instagram.com/fightforvaquita" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-pink-400 transition-colors duration-200"
            >
              <Instagram className="h-5 w-5" />
            </a>

            <button
              onClick={() => setTranslateOpen(!translateOpen)}
              className="text-gray-300 hover:text-blue-400 transition-colors duration-200 p-1"
              title="Translate"
            >
              <Globe className="h-5 w-5" />
            </button>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-gray-300 hover:text-blue-400 hover:bg-gray-800 transition-colors duration-200"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-gray-900 border-t border-gray-700">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  location.pathname === item.href
                    ? 'text-blue-400 bg-gray-800'
                    : 'text-gray-300 hover:text-blue-400 hover:bg-gray-800'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <Link to="/action" onClick={() => setIsOpen(false)}>
              <Button className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white font-medium py-2 rounded">
                Take Action
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;